/**
 * History Subject-First 场景Mock
 * 覆盖主文档任务: T-H1, T-H2, T-H3, T-H4, T-H5, T-H6
 * - T-H1: History列表读取与mapper
 * - T-H2: History点击打开result改为subject-first
 * - T-H3: ResultView主加载入口改为"先看subject，再决定legacy/solo兼容分支"
 * - T-H4: useFeatureUnlockStore主键从hash::preset切到subjectId优先
 * - T-H5: History回跳前unlock sync改成subject维度同步
 * - T-H6: 旧useResultRelation状态机下沉为legacy fallback模块
 */

import { delay, getMockDelay } from '../utils';
import { standardSubjectReady, subjectWithUpgradeOffer } from '../fixtures/subjects';
import {
    historyLegacyItems,
    historySpecialItems,
    buildHistoryListResponse,
    findHistoryItemByHashId,
    findHistoryItemBySubjectId
} from '../fixtures/history';

export const historySubjectFirstScenario = {
    name: 'history_subject_first',
    description: 'History列表与navigation的subject-first改造: T-H1~H6',

    // 场景状态
    state: {
        // 记录已同步的unlock状态
        syncedScopes: new Map(),
        // 记录legacy relation操作
        legacyOperations: new Map()
    },

    /**
     * 重置场景状态
     */
    reset() {
        this.state.syncedScopes.clear();
        this.state.legacyOperations.clear();
    },

    handlers: {
        // ==========================================
        // T-H1: History列表接口
        // ==========================================

        /**
         * T-H1: GET /photos/ 返回带subject_id/is_legacy的列表
         */
        async 'GET /photos/'(_payload, _context) {
            await delay(getMockDelay() * 0.5);

            // 模拟分页参数处理
            return buildHistoryListResponse();
        },

        // ==========================================
        // T-H2/T-H3: Subject优先的Detail查询
        // ==========================================

        /**
         * T-H3: 有subject_id时返回完整subject
         * GET /analysis/subjects/<subject_id>/
         */
        async 'GET /analysis/subjects/:id'(_payload, context) {
            await delay(getMockDelay());

            const subjectId = context?.params?.id;
            const historyItem = findHistoryItemBySubjectId(subjectId);

            if (!historyItem) {
                // T-H3: 未找到subject时返回404，触发兼容分支
                const error = new Error('Subject not found');
                error.status = 404;
                error.code = 'subject_not_found';
                throw error;
            }

            // 根据item状态返回对应的subject详情
            if (historyItem.current_visible_result_status === 'ready') {
                // 返回AI完成的subject
                return subjectWithUpgradeOffer({
                    subjectId: historyItem.subject_id,
                    originalHashId: historyItem.hash_id,
                    referenceCode: historyItem.reference_code
                });
            }

            // 返回待执行状态的subject
            return standardSubjectReady({
                subjectId: historyItem.subject_id,
                originalHashId: historyItem.hash_id,
                referenceCode: historyItem.reference_code
            });
        },

        // ==========================================
        // T-H6: Legacy兼容接口
        // ==========================================

        /**
         * T-H6: 旧hash-detail查询（兼容接口）
         * GET /photos/<hashId>/
         */
        async 'GET /photos/:hashId'(_payload, context) {
            await delay(getMockDelay());

            const hashId = context?.params?.hashId;
            const legacyItem = historyLegacyItems.find(l => l.hash_id === hashId);

            if (!legacyItem) {
                const error = new Error('Legacy item not found');
                error.status = 404;
                error.code = 'legacy_not_found';
                throw error;
            }

            // 模拟403: 无权访问
            const forbidden = historySpecialItems.forbidden.find(f => f.hash_id === hashId);
            if (forbidden) {
                const error = new Error('Access forbidden');
                error.status = 403;
                error.code = 'access_forbidden';
                error.reason = forbidden.reason;
                throw error;
            }

            // 模拟410: 永久删除
            const deleted = historySpecialItems.deleted.find(d => d.hash_id === hashId);
            if (deleted) {
                const error = new Error('Resource permanently deleted');
                error.status = 410;
                error.code = 'resource_deleted';
                error.deleted_at = deleted.deleted_at;
                throw error;
            }

            return {
                hash_id: legacyItem.hash_id,
                preset_style_id: legacyItem.preset_style_id,
                is_legacy: true,
                relation_status: legacyItem.relation_status,
                reference_data: {
                    style_id: legacyItem.preset_style_id,
                    style_name: 'Legacy Style',
                    category: 'portrait'
                },
                // 旧relation结构
                relation_data: {
                    original_hash_id: legacyItem.hash_id,
                    status: legacyItem.relation_status,
                    created_at: legacyItem.created_at
                }
            };
        },

        /**
         * T-H6: POST /photos/ 创建relation（solo入口）
         */
        async 'POST /photos/'(payload) {
            await delay(getMockDelay());

            const { hash_id, preset_style_id } = payload;

            // 模拟403: 无权访问
            const forbidden = historySpecialItems.forbidden.find(f => f.hash_id === hash_id);
            if (forbidden) {
                const error = new Error('Access forbidden');
                error.status = 403;
                error.code = 'access_forbidden';
                throw error;
            }

            // 模拟409: 已存在relation
            const existing = findHistoryItemByHashId(hash_id);
            if (existing && existing.is_legacy) {
                const error = new Error('Relation already exists');
                error.status = 409;
                error.code = 'relation_already_exists';
                throw error;
            }

            // 记录操作
            this.state.legacyOperations.set(hash_id, {
                action: 'create',
                preset_style_id,
                timestamp: Date.now()
            });

            return {
                hash_id,
                preset_style_id,
                relation_status: 'active',
                is_new: true,
                is_solo: !preset_style_id, // T-H1: 无reference = solo
                created_at: new Date().toISOString()
            };
        },

        /**
         * T-H6: POST /photos/<hash>/restore/ 恢复relation
         */
        async 'POST /photos/:hashId/restore/'(payload, context) {
            await delay(getMockDelay());

            const hashId = context?.params?.hashId;
            const { preset_style_id } = payload || {};

            // 模拟409: relation已存在且激活
            const existing = historyLegacyItems.find(
                l => l.hash_id === hashId && l.relation_status === 'active'
            );
            if (existing) {
                const error = new Error('Relation already exists and active');
                error.status = 409;
                error.code = 'relation_already_active';
                throw error;
            }

            // 模拟410: 资源已永久删除
            const deleted = historySpecialItems.deleted.find(d => d.hash_id === hashId);
            if (deleted) {
                const error = new Error('Resource permanently deleted');
                error.status = 410;
                error.code = 'resource_deleted';
                throw error;
            }

            // 模拟404: 不存在
            const notFound = historySpecialItems.notFound.find(n => n.hash_id === hashId);
            if (notFound) {
                const error = new Error('Resource not found');
                error.status = 404;
                error.code = 'resource_not_found';
                throw error;
            }

            // 记录操作
            this.state.legacyOperations.set(hashId, {
                action: 'restore',
                preset_style_id,
                timestamp: Date.now()
            });

            return {
                hash_id: hashId,
                preset_style_id,
                relation_status: 'active',
                is_restored: true,
                is_legacy: true,
                restored_at: new Date().toISOString()
            };
        },

        /**
         * T-H6: POST /photos/<hash>/ touch/update
         */
        async 'POST /photos/:hashId/'(payload, context) {
            await delay(getMockDelay());

            const hashId = context?.params?.hashId;
            const { preset_style_id, action } = payload || {};

            // 模拟404: relation不存在
            const existing = findHistoryItemByHashId(hashId);
            if (!existing && action !== 'create') {
                const error = new Error('Relation not found');
                error.status = 404;
                error.code = 'relation_not_found';
                throw error;
            }

            // 模拟410: relation已软删除
            if (existing?.relation_status === 'deleted') {
                const error = new Error('Relation deleted');
                error.status = 410;
                error.code = 'relation_deleted';
                throw error;
            }

            // 记录操作
            this.state.legacyOperations.set(hashId, {
                action: action || 'touch',
                preset_style_id,
                timestamp: Date.now()
            });

            return {
                hash_id: hashId,
                preset_style_id: preset_style_id || existing?.preset_style_id,
                relation_status: 'active',
                updated_at: new Date().toISOString(),
                is_touched: action === 'touch',
                is_legacy: true
            };
        },

        // ==========================================
        // T-H4/T-H5: Unlock Scope 同步接口
        // ==========================================

        /**
         * T-H4: GET /analysis/unlock-scope/ scope切换支持
         */
        async 'GET /analysis/unlock-scope/'(_payload, context) {
            await delay(getMockDelay() * 0.3);

            const { subject_id, hash_id, preset_style_id } = context?.params || {};

            if (subject_id) {
                // Subject维度scope（新主链路）
                return {
                    scope_type: 'subject',
                    scope_key: `subject:${subject_id}`,
                    subject_id,
                    unlock_state: {
                        capabilities: ['result_guide', 'compare'],
                        locked: ['summary', 'result_task_exposure'],
                        unavailable: ['hue', 'clipping', 'zone_boxplot']
                    },
                    tier_benefits: {
                        free: ['result_guide'],
                        standard: ['result_guide', 'compare'],
                        pro: ['result_guide', 'compare', 'summary', 'result_task_exposure']
                    }
                };
            }

            // Legacy兼容scope（hash::preset）
            return {
                scope_type: 'legacy',
                scope_key: `${hash_id}::${preset_style_id || 'default'}`,
                hash_id,
                preset_style_id,
                unlock_state: {
                    capabilities: ['result_guide'],
                    locked: ['compare', 'summary']
                },
                warning: 'legacy_scope_deprecation'
            };
        },

        /**
         * T-H5: POST /analysis/unlock-sync/ History回跳前unlock sync
         */
        async 'POST /analysis/unlock-sync/'(payload) {
            await delay(getMockDelay() * 0.3);

            const { subject_id, hash_id, preset_style_id } = payload;

            const syncRecord = {
                timestamp: Date.now(),
                success: true,
                sync_method: subject_id ? 'subject_scope' : 'legacy_scope'
            };

            // 记录sync状态
            const scopeKey = subject_id
                ? `subject:${subject_id}`
                : `${hash_id}::${preset_style_id || 'default'}`;
            this.state.syncedScopes.set(scopeKey, syncRecord);

            if (subject_id) {
                // T-H5: 带subjectId时走subject scope sync
                return {
                    sync_status: 'success',
                    scope: {
                        type: 'subject',
                        key: scopeKey,
                        subject_id
                    },
                    unlock_state: {
                        hydrated: true,
                        capabilities: [
                            { code: 'result_guide', availability: 'available' },
                            { code: 'compare', availability: 'available' },
                            { code: 'summary', availability: 'locked', unlock_requirements: { credits: 5 } },
                            { code: 'result_task_exposure', availability: 'locked', unlock_requirements: { credits: 3 } }
                        ]
                    },
                    sync_record: syncRecord,
                    failover_available: true
                };
            }

            // T-H5: subjectId缺失时，用hash::preset兼容sync
            return {
                sync_status: 'success',
                scope: {
                    type: 'legacy',
                    key: scopeKey,
                    hash_id,
                    preset_style_id
                },
                unlock_state: {
                    hydrated: true,
                    capabilities: [
                        { code: 'result_guide', availability: 'available' },
                        { code: 'compare', availability: 'available' }
                    ]
                },
                sync_record: syncRecord,
                failover_available: true,
                warning: 'legacy_scope_deprecation'
            };
        },

        // ==========================================
        // T-H2: Navigation路由构造决策
        // ==========================================

        /**
         * T-H2: History点击Navigation路由构造决策
         * 辅助接口：用于确定navigation路径
         */
        async 'GET /analysis/navigation-route/'(_payload, context) {
            const { subject_id, hash_id, is_legacy, preset_style_id, from } = context?.params || {};

            // 优先subject-first路径
            if (subject_id) {
                return {
                    route: `/result/${hash_id}`,
                    query: {
                        subjectId: subject_id,
                        from: from || 'history'
                    },
                    navigation_type: 'subject_first',
                    subject_id
                };
            }

            // T-H3: subject_id缺失时，按is_legacy分流
            if (is_legacy === true || is_legacy === 'true') {
                return {
                    route: `/result/${hash_id}`,
                    query: {
                        presetStyleId: preset_style_id,
                        from: from || 'history',
                        isLegacy: true
                    },
                    navigation_type: 'legacy_fallback',
                    hash_id,
                    preset_style_id
                };
            }

            // Solo兼容路径（无reference）
            return {
                route: `/result/${hash_id}`,
                query: {
                    from: from || 'history',
                    solo: true
                },
                navigation_type: 'solo_compatible',
                hash_id
            };
        }
    }
};
